import React, { useState, useEffect } from 'react';
import api from '../api/client';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MonthlyPlanTable = ({ products, refreshKey }) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthNum = currentDate.getMonth() + 1;
  const allYears = [currentYear, currentYear + 1];

  const currentYearMonths = months.slice(currentMonthNum - 1);
  const nextYearMonths = months.slice(0, currentMonthNum);

  const [planData, setPlanData] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [cellValue, setCellValue] = useState('');

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const { data } = await api.get(`/plantoproduce/quantities?years=${allYears.join(',')}`);
        const map = {};
        data.forEach(({ sku, year, month_num, to_produce_qty }) => {
          map[`${sku}-${year}-${month_num}`] = to_produce_qty;
        });
        setPlanData(map);
      } catch (err) {
        console.error('Error fetching monthly plan:', err);
      }
    };
    fetchPlan();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const saveCell = async (sku, year, monthNum, value) => {
    const qty = value.toString().trim() === '' ? 0 : parseInt(value) || 0;
    try {
      await api.post('/plantoproduce/quantity', { sku, year, month_num: monthNum, to_produce_qty: qty });
      setPlanData(prev => ({ ...prev, [`${sku}-${year}-${monthNum}`]: qty }));
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save. Please try again.');
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e, sku, year, monthNum) => {
    if (e.key === 'Enter') saveCell(sku, year, monthNum, cellValue);
    if (e.key === 'Escape') setEditingCell(null);
  };

  const totalCols = 4 + currentYearMonths.length + nextYearMonths.length;

  return (
    <div className="plan-table-container">
      <div className="plan-table-wrapper">
        <table className="plan-table">
          <thead>
            <tr>
              <th rowSpan="2" className="sticky col-row header-sticky">#</th>
              <th rowSpan="2" className="sticky col-name header-sticky left-col2">Name</th>
              <th rowSpan="2" className="header-sticky col-sku">SKU</th>
              <th rowSpan="2" className="header-sticky col-asin">ASIN</th>
              {allYears.map((year, i) => (
                <th
                  key={year}
                  colSpan={i === 0 ? currentYearMonths.length : nextYearMonths.length}
                  className="year-header header-sticky"
                >
                  {year}
                </th>
              ))}
            </tr>
            <tr>
              {allYears.map((year, i) =>
                (i === 0 ? currentYearMonths : nextYearMonths).map(month => (
                  <th key={`${year}-${month}`} className="month-header header-sticky">
                    {month}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={totalCols} className="ptp-empty-row">
                  <div className="ptp-empty-state">
                    <i className="fas fa-search" style={{ fontSize: 24, opacity: 0.25 }}></i>
                    <span>No products match your filters</span>
                  </div>
                </td>
              </tr>
            ) : products.map((product, index) => (
              <tr key={product.SKU} className="ptp-data-row">
                <td className="sticky col-row">{index + 1}</td>
                <td className="sticky col-name left-col2">{product.name}</td>
                <td className="col-sku">{product.SKU}</td>
                <td className="col-asin">{product.ASIN}</td>
                {allYears.map((year, yearIndex) =>
                  (yearIndex === 0 ? currentYearMonths : nextYearMonths).map((month, monthIndex) => {
                    const monthNum = yearIndex === 0
                      ? currentMonthNum + monthIndex
                      : monthIndex + 1;
                    const cellKey = `${product.SKU}-${year}-${monthNum}`;
                    const val = planData[cellKey];
                    const isEditing = editingCell === cellKey;
                    return (
                      <td
                        key={cellKey}
                        className={`qty-cell${val ? ' has-value' : ''}${isEditing ? ' editing' : ''}`}
                        onDoubleClick={() => {
                          if (!isEditing) {
                            setEditingCell(cellKey);
                            setCellValue(val || '');
                          }
                        }}
                      >
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            className="cell-input"
                            value={cellValue}
                            onChange={e => setCellValue(e.target.value)}
                            onKeyDown={e => handleKeyDown(e, product.SKU, year, monthNum)}
                            onBlur={() => saveCell(product.SKU, year, monthNum, cellValue)}
                            autoFocus
                            onClick={e => e.stopPropagation()}
                          />
                        ) : (
                          <span className="cell-display">{val || '—'}</span>
                        )}
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="ptp-totals-row">
              <td className="sticky col-row totals-label" colSpan="2">Total</td>
              <td className="col-sku"></td>
              <td className="col-asin"></td>
              {allYears.map((year, yearIndex) =>
                (yearIndex === 0 ? currentYearMonths : nextYearMonths).map((month, monthIndex) => {
                  const monthNum = yearIndex === 0
                    ? currentMonthNum + monthIndex
                    : monthIndex + 1;
                  const total = products.reduce((sum, p) => {
                    const val = planData[`${p.SKU}-${year}-${monthNum}`];
                    return sum + (val ? Number(val) : 0);
                  }, 0);
                  return (
                    <td key={`total-${year}-${monthNum}`} className="qty-cell totals-cell">
                      {total > 0 ? total.toLocaleString() : '—'}
                    </td>
                  );
                })
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default MonthlyPlanTable;